'use strict';

/**
 * NACRE Wave 6.2 — pin-client (browser-side PIN crypto)
 *
 * The browser-side half of the per-module PIN protection model. Pure
 * functions, no I/O, browser-safe (only @noble/* deps via the kdf +
 * symmetric primitives).
 *
 * The architecture (Interpretation B from the Wave 6 design discussion):
 *
 *   Setup PIN:
 *     1. User enters PIN
 *     2. Browser generates a fresh saltPin (16 random bytes)
 *     3. Browser derives pinKey = Argon2id(pin, saltPin)
 *     4. Browser encrypts the existing moduleMaster under pinKey
 *     5. Browser POSTs the resulting envelope (with saltPin embedded) to
 *        the server's PUT /auth/vault/me/pin/:moduleId endpoint
 *     6. Browser wipes the moduleMaster from session memory
 *
 *   Unlock:
 *     1. User opens a PIN-protected module
 *     2. Browser fetches the envelope from GET /auth/vault/me/pin/:moduleId
 *     3. Browser prompts for PIN
 *     4. Browser derives pinKey using the saltPin from the envelope
 *     5. Browser unwraps the moduleMaster
 *     6. Browser holds it in session memory until auto-lock
 *
 *   Auto-lock:
 *     1. Timer fires after N minutes of inactivity
 *     2. Browser wipes the moduleMaster from session memory
 *     3. Next module access requires the PIN again
 *
 * The server NEVER sees: the PIN, pinKey, or unwrapped moduleMaster.
 * The wrap envelope is opaque to the server.
 *
 * ⚠ T1123 — THE ENVELOPE ALSO CARRIES `removeProof`, AND IT IS LOAD-BEARING.
 * `sha256(moduleMaster || salt)` is a WRITE-ONLY proof-of-knowledge verifier:
 * the server stores it and compares against it, and only a client that has
 * UNWRAPPED the master can recompute it. Four consumers read it — the strongbox
 * step-up, the DELETE-lock gate, the PSA-17b replacement gate, and the
 * `FEATURE_MODULE_LOCK` blob read/write gate — and every one of them fails
 * CLOSED when it is absent.
 *
 * Measured at trunk `c632affa3`: these two builders were the shipped setup path
 * and NEITHER recorded it (`grep -c -a 'removeProof' public/auth/vault-bundle.js`
 * -> 0, with `RemoveProof` -> 5 proving the matcher works and only function
 * NAMES were present). So `FEATURE_MODULE_LOCK` could not be flipped even by a
 * perfect client: `expectedProof` was null for every module a user had locked
 * through the UI, the gate refused unconditionally, and no proof could clear
 * it. Wiring the client's `unlockProof` alone would have fixed nothing.
 *
 * It is derived here, at the same moment and from the same bytes as the wrap,
 * because that is the only place both the master and the fresh salt exist. It
 * discloses nothing the envelope did not already imply: it is a one-way hash of
 * a value the holder must already possess to use the envelope at all.
 *
 * Public API:
 *   - derivePinKey(pin, salt, kdfProfile?) → Uint8Array
 *   - wrapModuleMasterWithPin(moduleMaster, pinKey) → envelope
 *   - unwrapModuleMasterWithPin(envelope, pinKey) → Uint8Array
 *   - validatePinFormat(pin) → {ok, error?}
 *   - buildPinWrapEnvelope({pin, moduleMaster, kdfProfile?}) → full envelope (incl saltPin)
 *   - unwrapPinEnvelope({pin, pinWrap, kdfProfile?}) → Uint8Array
 */

const crypto = require('crypto');
const { argon2id, KDF_PROFILES } = require('./kdf');
const symmetric = require('./symmetric');
const { computeRemoveProof } = require('../module-lock/remove-proof');

const PIN_LABEL = 'oyster:vault:pin:v1';

/**
 * Derive a 32-byte key from a PIN and salt using Argon2id.
 *
 * @param {string} pin
 * @param {Uint8Array|Buffer} salt — must be at least 16 bytes
 * @param {object} [kdfProfile] — Argon2id parameters (defaults to DESKTOP)
 * @returns {Uint8Array} 32-byte key
 */
function derivePinKey(pin, salt, kdfProfile) {
  if (typeof pin !== 'string' || pin.length === 0) {
    throw new Error('Invalid PIN: must be a non-empty string');
  }
  return argon2id(pin, salt, kdfProfile || KDF_PROFILES.DESKTOP);
}

/**
 * Encrypt a module master under a PIN-derived key.
 *
 * @param {Uint8Array} moduleMaster — 32 bytes
 * @param {Uint8Array} pinKey — 32 bytes from derivePinKey
 * @returns {{algo, iv, ct, tag}} envelope (no saltPin — caller adds it)
 */
function wrapModuleMasterWithPin(moduleMaster, pinKey) {
  if (!(moduleMaster instanceof Uint8Array) && !Buffer.isBuffer(moduleMaster)) {
    throw new TypeError('moduleMaster must be Uint8Array or Buffer');
  }
  if (moduleMaster.length !== 32) {
    throw new Error('moduleMaster must be exactly 32 bytes');
  }
  return symmetric.encrypt(moduleMaster, pinKey);
}

/**
 * Decrypt a module master from a PIN-wrap envelope.
 *
 * @param {{algo, iv, ct, tag}} envelope
 * @param {Uint8Array} pinKey — 32 bytes from derivePinKey
 * @returns {Uint8Array} 32-byte module master
 */
function unwrapModuleMasterWithPin(envelope, pinKey) {
  return symmetric.decrypt(envelope, pinKey, { binary: true });
}

/**
 * Validate a PIN candidate. Returns {ok: true} or {ok: false, error: '...'}.
 *
 * Rules (per Wave 6 design defaults):
 *   - At least 4 characters
 *   - Maximum 64 characters
 *   - Allowed: letters, digits, common punctuation (no whitespace, no control chars)
 *   - Cannot be all the same character (1111, aaaa, etc.)
 *
 * @param {string} pin
 * @returns {{ok: boolean, error?: string}}
 */
function validatePinFormat(pin) {
  if (typeof pin !== 'string' || pin.length === 0) {
    return { ok: false, error: 'PIN cannot be empty' };
  }
  if (pin.length < 4) {
    return { ok: false, error: 'PIN must be at least 4 characters' };
  }
  if (pin.length > 64) {
    return { ok: false, error: 'PIN must be at most 64 characters' };
  }
  // All same character check (e.g., 1111, aaaaaa)
  if (/^(.)\1+$/.test(pin)) {
    return { ok: false, error: 'PIN cannot be all the same character' };
  }
  // Allowed characters: printable ASCII excluding whitespace
  if (!/^[\x21-\x7e]+$/.test(pin)) {
    return { ok: false, error: 'PIN cannot contain whitespace or control characters' };
  }
  return { ok: true };
}

/**
 * Convenience: full wrap flow. Generates a fresh salt, derives the key,
 * wraps the master, and returns the complete envelope (with saltPin
 * embedded so the server can serve it back to the browser later).
 *
 * @param {object} args
 * @param {string} args.pin
 * @param {Uint8Array} args.moduleMaster
 * @param {object} [args.kdfProfile]
 * @returns {{algo, iv, ct, tag, saltPin, removeProof}}
 */
function buildPinWrapEnvelope({ pin, moduleMaster, kdfProfile }) {
  if (!(moduleMaster instanceof Uint8Array) && !Buffer.isBuffer(moduleMaster)) {
    throw new TypeError('moduleMaster must be Uint8Array or Buffer');
  }
  const saltBytes = crypto.randomBytes(16);
  const pinKey = derivePinKey(pin, saltBytes, kdfProfile);
  const env = wrapModuleMasterWithPin(moduleMaster, pinKey);
  const saltHex = Buffer.from(saltBytes).toString('hex');
  return {
    ...env,
    saltPin: saltHex,
    removeProof: computeRemoveProof(moduleMaster, saltHex),
  };
}

/**
 * Convenience: full unwrap flow. Extracts the saltPin from the envelope,
 * derives the key, and unwraps the master.
 *
 * @param {object} args
 * @param {string} args.pin
 * @param {{algo, iv, ct, tag, saltPin}} args.pinWrap
 * @param {object} [args.kdfProfile]
 * @returns {Uint8Array} 32-byte module master
 */
function unwrapPinEnvelope({ pin, pinWrap, kdfProfile }) {
  if (!pinWrap || typeof pinWrap !== 'object') {
    throw new Error('Invalid pinWrap: must be an object');
  }
  if (typeof pinWrap.saltPin !== 'string' || pinWrap.saltPin.length < 32) {
    throw new Error('Invalid pinWrap: missing or malformed saltPin');
  }
  if (pinWrap.algo === undefined || pinWrap.iv === undefined ||
      pinWrap.ct === undefined || pinWrap.tag === undefined) {
    throw new Error('Invalid pinWrap envelope: missing required fields');
  }
  const saltBytes = Buffer.from(pinWrap.saltPin, 'hex');
  const pinKey = derivePinKey(pin, saltBytes, kdfProfile);
  return unwrapModuleMasterWithPin(pinWrap, pinKey);
}

// ── T576 (LD-2/LD-3) — Vault KEY-named API ────────────────────────────────
// The credential is a "Vault Key" (formerly "PIN"). New envelopes carry the
// salt as `saltKey`; unwrap UNION-reads `saltKey` OR the legacy `saltPin`
// (no-brick). The KDF (derivePinKey) + domain label are UNTOUCHED — a Key IS a
// PIN cryptographically; only the naming changed.

/**
 * Full wrap flow (KEY-named). Generates a fresh salt, derives the key, wraps
 * the master, returns the complete envelope with `saltKey` embedded.
 * @param {object} args
 * @param {string} args.key   the Vault Key (accepts legacy `pin` alias)
 * @param {Uint8Array} args.moduleMaster
 * @param {object} [args.kdfProfile]
 * @returns {{algo, iv, ct, tag, saltKey, removeProof}}
 */
function buildKeyWrapEnvelope({ key, pin, moduleMaster, kdfProfile }) {
  if (!(moduleMaster instanceof Uint8Array) && !Buffer.isBuffer(moduleMaster)) {
    throw new TypeError('moduleMaster must be Uint8Array or Buffer');
  }
  const secret = (typeof key === 'string' && key.length) ? key : pin;
  const saltBytes = crypto.randomBytes(16);
  const derived = derivePinKey(secret, saltBytes, kdfProfile);
  const env = wrapModuleMasterWithPin(moduleMaster, derived);
  const saltHex = Buffer.from(saltBytes).toString('hex');
  return { ...env, saltKey: saltHex, removeProof: computeRemoveProof(moduleMaster, saltHex) };
}

/**
 * Full unwrap flow (KEY-named). UNION salt: accepts an envelope carrying
 * `saltKey` (new) OR `saltPin` (legacy). Accepts a `key` or legacy `pin` arg.
 * @param {object} args
 * @param {string} args.key   the Vault Key (accepts legacy `pin` alias)
 * @param {object} args.keyWrap  the envelope (accepts legacy `pinWrap` alias)
 * @param {object} [args.kdfProfile]
 * @returns {Uint8Array} 32-byte module master
 */
function unwrapKeyEnvelope({ key, pin, keyWrap, pinWrap, kdfProfile }) {
  const env = keyWrap || pinWrap;
  const secret = (typeof key === 'string' && key.length) ? key : pin;
  if (!env || typeof env !== 'object') {
    throw new Error('Invalid keyWrap: must be an object');
  }
  const saltHex = (typeof env.saltKey === 'string') ? env.saltKey
    : (typeof env.saltPin === 'string') ? env.saltPin : null;
  if (saltHex === null || saltHex.length < 32) {
    throw new Error('Invalid keyWrap: missing or malformed saltKey');
  }
  if (env.algo === undefined || env.iv === undefined ||
      env.ct === undefined || env.tag === undefined) {
    throw new Error('Invalid keyWrap envelope: missing required fields');
  }
  const saltBytes = Buffer.from(saltHex, 'hex');
  const derived = derivePinKey(secret, saltBytes, kdfProfile);
  return unwrapModuleMasterWithPin(env, derived);
}

module.exports = {
  derivePinKey,
  wrapModuleMasterWithPin,
  unwrapModuleMasterWithPin,
  validatePinFormat,
  buildPinWrapEnvelope,
  unwrapPinEnvelope,
  PIN_LABEL,
  // T576 Key-named API + legacy aliases for the same behavior.
  buildKeyWrapEnvelope,
  unwrapKeyEnvelope,
  validateKeyFormat: validatePinFormat,
};
